<script setup name="TvbHomeEpg">
import { inject, reactive, computed, onServerPrefetch } from 'vue'
import dayjs from 'dayjs'
import zhHK from 'dayjs/locale/zh-hk'
import { getMyTvChannels } from './../../../../api/home'

dayjs.locale(zhHK)

defineProps({
  info: {
    type: Object,
    default: null
  }
})

const size = inject('size')
const width = inject('width')
const epg = reactive({
  loading: false,
  list: [],
  more_url: '',
  time: dayjs(),
  total: null
})
const isShowRefresh = computed(() => width.value > 1024)
const isMobile = computed(() => ['s', 'xs'].includes(size.value))

const getEpg = async () => {
  if (epg.loading) return
  epg.loading = true
  try {
    const { data } = await getMyTvChannels()
    for (const key in data) {
      if (key === 'time') {
        data[key] = dayjs(data[key] * 1000)
      }
      epg[key] = data[key]
    }
    if (!import.meta.env.SSR) {
      setTimeout(() => {
        epg.loading = false
      }, 1500)
    }
  } catch (error) {
    console.error(error)
    epg.loading = false
  }
}

getEpg()
onServerPrefetch(getEpg)
</script>

<template>
  <div class="tvb-home-epg" :class="size">
    <header class="tvb-home-epg-header">
      <h2 class="tvb-home-epg-title">
        <component :is="epg?.more_url ? 'tvb-router-link' : 'span'" :to="epg?.more_url">
          {{ info.title }}
        </component>
      </h2>
      <button
        class="tvb-home-epg-refresh"
        @click="getEpg"
        :disabled="epg.loading"
        v-if="isShowRefresh"
      >
        <n-spin :rotate="epg.loading" content-class="tvb-home-epg-refresh-icon">
          <template #icon>
            <TvbSvgIcon name="icon_epg_refresh_black" />
          </template>
        </n-spin>
        <span>{{ epg.loading ? '正在更新' : '按此更新' }}</span>
      </button>
      <tvb-button
        class="tvb-home-epg_more"
        :type="!isMobile ? 'more' : 'more-text'"
        :animation="!isMobile ? 'more' : ''"
        icon="icon_right_more"
        :path="epg.more_url"
        :style="{ '--tvb-button-height': isMobile ? '24px' : '36px' }"
        >查看更多</tvb-button
      >
    </header>
    <main class="tvb-home-epg-main">
      <div class="tvb-home-epg-items">
        <div class="tvb-home-epg-item" @click="getEpg">
          <div class="tvb-home-epg-time">
            <time class="tvb-home-epg-yyyy" :datetime="epg.time.toString()">
              {{ epg.time.format('YYYY-MM-DD') }}({{ epg.time.format('dd') }})
            </time>
            <time class="tvb-home-epg-hh" :datetime="epg.time.toString()">
              {{ epg.time.format('hh:mm A') }}
            </time>
          </div>
          <button
            class="tvb-home-epg-mobile-refresh"
            @click="getEpg"
            :disabled="epg.loading"
            v-if="!isShowRefresh"
          >
            <n-spin :rotate="epg.loading" content-class="tvb-home-epg-refresh-icon">
              <template #icon>
                <TvbSvgIcon name="icon_epg_refresh" />
              </template>
            </n-spin>
            <span>{{ epg.loading ? '正在更新' : '按此更新' }}</span>
          </button>
        </div>
        <a
          class="tvb-home-epg-item"
          v-for="(item, index) in epg.list"
          :href="item.jump_url"
          :key="index"
        >
          <tvb-image
            :is-loading-icon="false"
            class="tvb-home-epg-icon"
            :src="`/image/img_epg_${item.channel_no}_logo@3x.png`"
            :alt="`${item.name_tc}`"
          />
          <p
            class="tvb-home-epg-programme-title"
            :title="`${item.current_programme.programme_title_tc}`"
          >
            <span>{{ item.current_programme.programme_title_tc }}</span>
          </p>
        </a>
      </div>
    </main>
  </div>
</template>

<style lang="scss" scoped>
.tvb-home-epg {
  // margin-top: 56px;
  min-width: 320px;
  .tvb-home-epg-header {
    display: flex;
    align-items: center;

    .tvb-home-epg-title {
      font-size: 22px;
      font-weight: 500;
      color: #231f20;
      line-height: 36px;
      letter-spacing: 0.5px;
      @include line-text-ellipsis(1);
      > a {
        text-decoration: none;
        color: inherit;
        transition: color 0.15s linear;
        @include text-hover-before();
        &:hover {
          @include text-hover-active();
        }
      }
    }

    .tvb-home-epg-refresh {
      height: 32px;
      background: #f5f6f7;
      border-radius: 12px;
      border: 0;
      outline: 0;
      padding: 0 12px 0 10px;
      cursor: pointer;
      margin-left: 12px;
      font-size: 14px;
      font-weight: 400;
      color: #231f20;
      line-height: 20px;
      letter-spacing: 0.3px;
      display: flex;
      align-items: center;
      justify-content: center;

      &:active {
        opacity: 0.8;
      }
      &:hover {
        color: #231f20;
        background-color: #eceff3;
      }

      :deep(.n-spin) {
        --n-size: 16px !important;
        &.n-spin--rotate {
          animation: spin-rotate 1s linear infinite !important;
        }
      }

      // svg {
      // }
      :deep(.n-spin-body) {
        margin-right: 2px;
      }
    }

    :deep(.tvb-home-epg_more) {
      margin-left: auto;

      &.btn-more-text {
        position: relative;
        top: 1px;
      }
    }
  }

  &.s,
  &.xs {
    // margin-top: 44px;
    padding: 0 16px;
    .tvb-home-epg-header {
      .tvb-home-epg-title {
        font-size: 17px;
        line-height: 24px;
      }
    }
    :deep(.tvb-home-epg_more) {
      padding-left: 12px;
    }
  }
  &.l,
  &.m {
    .tvb-home-epg-header {
      .tvb-home-epg-title {
        font-size: 20px;
      }
    }
  }
  &.xs {
    padding: 0 12px;
  }

  .tvb-home-epg-main {
    margin-top: 16px;
    --epg-time-font-size: 18px;
    --epg-font-size: 18px;
    --epg-line-height: 26px;
    --epg-item-padding: 20px;
    --epg-items-padding: 16px;
    --epg-gap: 16px;
    --epg-icon-width: 100px;
    --epg-icon-height: 32px;
    --epg-border-radius: 24px;
    --epg-item-border-radius: 16px;
    --epg-programme-gap: 8px;
    --epg-time-width: 130px;
    --epg-first-item-width: 12.5%;
    --epg-first-item-min-width: 178px;
    --epg-refresh-font-size: 14px;
    --epg-refresh-icon: 16px;
    --epg-letter-spacing: 0.5px;

    .tvb-home-epg-items {
      display: flex;
      padding: var(--epg-items-padding);
      align-items: center;
      gap: var(--epg-gap);
      align-self: stretch;
      border-radius: var(--epg-border-radius);
      border: 0.5px solid rgba(163, 178, 188, 0.35);
      background: #fcfcfc;
      overflow: scroll;
      &::-webkit-scrollbar {
        display: none;
      }
      scrollbar-width: none;
      -ms-overflow-style: none;
      > a {
        text-decoration: none;
        color: inherit;
      }

      .tvb-home-epg-item {
        display: flex;
        flex-direction: column;
        align-self: stretch;
        border-radius: var(--epg-item-border-radius);
        background: #fff;

        .tvb-home-epg-time {
          text-align: left;
          color: rgba(255, 255, 255, 0.95);
          font-size: var(--epg-time-font-size);
          font-weight: 500;
          line-height: var(--epg-line-height);
          letter-spacing: 0.5px;
          display: flex;
          flex-direction: column;
          .tvb-home-epg-hh {
            margin-top: 2px;
          }
        }
        &:first-child {
          width: var(--epg-first-item-width);
          min-width: var(--epg-first-item-min-width);
          padding: var(--epg-item-padding);
          background: #0055bd;
          align-items: center;
          justify-content: center;
        }
        &:not(:first-child) {
          padding: var(--epg-item-padding);
          align-items: flex-start;
          justify-content: flex-start;
          gap: var(--epg-programme-gap);
          flex: 1 0 0;
          align-self: stretch;
          outline: 0.5px solid rgba(163, 178, 188, 0.35);
        }
        &:last-child {
          margin-right: 0;
        }

        .tvb-home-epg-icon {
          aspect-ratio: 25/8;
          width: var(--epg-icon-width);
          height: auto;
          :deep(.tvb-home-epg-icon) {
            aspect-ratio: 25/8;
            width: var(--epg-icon-width);
            height: auto;
          }
        }

        .tvb-home-epg-programme-title {
          font-size: var(--epg-font-size);
          font-weight: 400;
          color: #231f20;
          line-height: var(--epg-line-height);
          letter-spacing: 0.5px;
          word-break: break-all;
          @include line-text-ellipsis(2);
          span {
            @include text-hover-before();
            &:hover {
              @include text-hover-active();
            }
          }
        }
      }
    }
    @media (max-width: 1440px) and (min-width: 1025px) {
      --epg-item-padding: 18px;
      --epg-items-padding: 14px;
      --epg-gap: 14px;
      --epg-time-font-size: 17px;
      --epg-font-size: 17px;
      --epg-icon-width: 87px;
      --epg-icon-height: 28px;
      --epg-programme-gap: 6px;
      --epg-time-width: 123px;
      --epg-first-item-width: 13.535294%;
      --epg-first-item-min-width: 166px;
    }

    @media (max-width: 1024px) and (min-width: 1px) {
      --epg-time-font-size: 16px;
      --epg-font-size: 16px;
      --epg-line-height: 24px;
      --epg-icon-width: 75px;
      --epg-icon-height: 24px;
      --epg-border-radius: 16px;
      --epg-item-border-radius: 12px;
      --epg-item-padding: 14px 16px;
      --epg-items-padding: 14px;
      --epg-gap: 10px;
      --epg-first-item-width: 100%;
      --epg-refresh-font-size: 16px;
      --epg-refresh-icon: 16px;
      --epg-marigin-top: 16px;

      flex-direction: column;
      --tvb-hairline-border-radius: calc(2 * var(--epg-item-border-radius));
      margin-top: var(--epg-marigin-top);
      height: auto;
      .tvb-home-epg-items {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        .tvb-home-epg-item {
          width: 100%;
          align-self: stretch;
          flex-wrap: nowrap;
          flex-direction: row;
          &:first-child {
            display: flex;
            justify-content: space-between;
            max-width: 1024px;
          }
          &:not(:first-child) {
            min-width: 240px;
            gap: 12px;
          }

          .tvb-home-epg-time {
            display: flex;
            flex-wrap: nowrap;
            flex-direction: row;
            min-width: 210px;

            .tvb-home-epg-yyyy,
            .tvb-home-epg-hh {
              display: flex;
              text-align: justify;
              font-size: var(--epg-time-font-size);
              font-weight: 400;
              line-height: var(--epg-line-height);
              letter-spacing: var(--epg-letter-spacing);
              width: auto;
              margin-top: 0;
            }
            .tvb-home-epg-hh {
              margin-left: 8px;
            }
            &:active {
              opacity: 0.8;
            }
          }

          .tvb-home-epg-programme-title {
            margin-top: 0;
            font-size: var(--epg-font-size);
            font-weight: 400;
            color: #231f20;
            line-height: var(--epg-line-height);
            width: calc((100% - 68px));
            align-items: left;
            @include line-text-ellipsis(1);
          }

          .tvb-home-epg-mobile-refresh {
            display: flex;
            flex-wrap: nowrap;
            color: rgba(255, 255, 255, 0.95);
            background: none;
            border: none;
            font-size: var(--epg-refresh-font-size);
            font-weight: 400;
            line-height: var(--epg-line-height);
            letter-spacing: var(--epg-letter-spacing);
            align-items: center;
            &:active {
              opacity: 0.8;
            }

            :deep(.n-spin) {
              --n-size: var(--epg-refresh-icon) !important;
            }
            :deep(span) {
              margin-left: 2px;
            }
          }
        }
      }
    }
    @media (max-width: 768px) and (min-width: 1px) {
      --epg-time-font-size: 14px;
      --epg-font-size: 16px;
      --epg-line-height: 24px;
      --epg-icon-width: 69px;
      --epg-icon-height: 22px;
      --epg-item-padding: 12px 14px;
      --epg-items-padding: 12px;
      --epg-gap: 8px;
      --epg-refresh-font-size: 14px;
      --epg-refresh-icon: 14px;
      --epg-letter-spacing: 0.3px;
      --epg-marigin-top: 10px;
    }
  }
}
</style>
